import { requestGet } from "../utils/request.ts";
import { createRefreshResource, refreshParts, type RefreshResource } from "../utils/refresh_resource.ts";
import { $sessionVersion, getSessionVersion, refreshUserInfo } from "./user_info.ts";
import { updateCameraInfo } from "./cameras.ts";

export interface AIConfig {
    ai_enabled?: boolean;
    modules: { slug: string; name: string; description: string }[];
    selected_module_slug: string | null;
    cameras: { camera_id: number; camera_name: string; is_enabled: boolean }[];
    show_overview_ai_trend?: boolean;
    preferred_trend_days?: number;
}
export interface AIStat {
    date: string;
    avg_person: number;
    avg_vehicle: number;
}
export const aiConfigResource = createRefreshResource(
    signal => requestGet<AIConfig>("/../ai/config", {}, signal),
    null as AIConfig | null, getSessionVersion,
);
const stats = new Map<string, RefreshResource<AIStat[]>>();
export function getAIStatsResource(cameraId?: number, days = 7) {
    const key = `${cameraId ?? "all"}:${days}`;
    let resource = stats.get(key);
    if (!resource) {
        resource = createRefreshResource(async signal => {
            const resp = await requestGet<{ stats: AIStat[] }>("/../ai/stats", {
                camera_id: cameraId, days,
            }, signal);
            return resp.stats;
        }, [] as AIStat[], getSessionVersion);
        stats.set(key, resource);
    }
    return resource;
}
export const invalidateAI = () => {
    aiConfigResource.invalidate();
    for (const resource of stats.values()) resource.invalidate();
};
$sessionVersion.listen(() => {
    aiConfigResource.reset();
    for (const resource of stats.values()) resource.reset();
    stats.clear();
});

export async function refreshOverviewPage() {
    await refreshUserInfo();
    await refreshParts({
        cameras: updateCameraInfo(),
        AI: aiConfigResource.refresh().then(async config => {
            if (config?.ai_enabled && config.show_overview_ai_trend) {
                await getAIStatsResource(undefined, config.preferred_trend_days ?? 7).refresh();
            }
        }),
    });
}
